import { motion } from 'framer-motion'
import { V2 } from './theme'
import { V2Icon } from './V2Icon'

export interface V2Option<T extends string> {
  value: T
  label: string
  description?: string
  /** public/images/v2 icon name */
  icon?: string
}

interface SingleSelectV2Props<T extends string> {
  options: V2Option<T>[]
  value: T | null
  onChange: (value: T) => void
}

/** organic-v2 SingleSelect: same interaction as SingleSelect, app 4.33 option-card look. */
export function SingleSelectV2<T extends string>({ options, value, onChange }: SingleSelectV2Props<T>) {
  return (
    <div className="flex flex-col gap-3">
      {options.map((option, index) => {
        const isSelected = value === option.value

        return (
          <motion.button
            key={option.value}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onChange(option.value)}
            className={`w-full min-h-[56px] p-4 rounded-xl transition-all duration-200 border-2 text-left ${
              isSelected ? `border-primary ${V2.selectedBg}` : `${V2.border} bg-white hover:border-slate-300`
            }`}
          >
            <div className="flex items-center gap-4">
              {option.icon && <V2Icon name={option.icon} />}
              <div className="flex-1">
                <span className={`font-semibold text-[16px] ${V2.text}`}>{option.label}</span>
                {option.description && <p className={`text-sm mt-0.5 ${V2.textSecondary}`}>{option.description}</p>}
              </div>
              <div
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  isSelected ? 'border-primary bg-primary' : 'border-slate-300'
                }`}
              >
                {isSelected && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3.5 h-3.5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </motion.svg>
                )}
              </div>
            </div>
          </motion.button>
        )
      })}
    </div>
  )
}
